import { create, type StateCreator } from 'zustand'
import { ENV_VARS } from '@/helpers/constants'
import type { CreateMutable } from '@/helpers/types'

const { VITE_DISABLED_FEATURES } = ENV_VARS

export const FEATURES = {
  ADD_ROOM: 'ADD_ROOM',
  CALLS_ROUTE: 'CALLS_ROUTE',
  CALL_END_DIALOG: 'CALL_END_DIALOG',
  CHANGE_ROOMS: 'CHANGE_ROOMS',
  CHATS_ROUTE: 'CHATS_ROUTE',
  CREATE_SIDEBAR: 'CREATE_SIDEBAR',
  DEVELOPER_TOASTER_DIALOG: 'DEVELOPER_TOASTER_DIALOG', // Can be removed, for demonstrating toasts
  DEVELOPER_TOOLS: 'DEVELOPER_TOOLS',
  ERROR_DIALOG: 'ERROR_DIALOG',
  GLOBAL_ERROR_LISTENERS: 'GLOBAL_ERROR_LISTENERS',
  GLOBAL_TOASTER: 'GLOBAL_TOASTER',
  MEMBER_AUTO_GAIN_CONTROL: 'MEMBER_AUTO_GAIN_CONTROL',
  MEMBER_BACKGROUND_NOISE: 'MEMBER_BACKGROUND_NOISE',
  MEMBER_ECHO_CANCELLATION: 'MEMBER_ECHO_CANCELLATION',
  MEMBER_LOW_BITRATE_MODE: 'MEMBER_LOW_BITRATE_MODE',
  MEMBER_MICROPHONE_SENSITIVITY: 'MEMBER_MICROPHONE_SENSITIVITY',
  MEMBER_MICROPHONE_VOLUME: 'MEMBER_MICROPHONE_VOLUME',
  MEMBER_NOISE_SUPPRESSION: 'MEMBER_NOISE_SUPPRESSION',
  MEMBER_OVERLAY_ADVANCED: 'MEMBER_OVERLAY_ADVANCED',
  MEMBER_SET_AUDIO_FLAGS: 'MEMBER_SET_AUDIO_FLAGS',
  MEMBER_SPEAKER_VOLUME: 'MEMBER_SPEAKER_VOLUME',
  MEMBER_USER_DISPLAY_BANNER: 'MEMBER_USER_DISPLAY_BANNER',
  MEMBER_VIDEO_QUALITY: 'MEMBER_VIDEO_QUALITY',
  MIRROR_LOCAL_VIDEO: 'MIRROR_LOCAL_VIDEO',
  PARTICIPANTS_MEMBER_BUTTONS: 'PARTICIPANTS_MEMBER_BUTTONS',
  PARTICIPANTS_MUTE_ALL: 'PARTICIPANTS_MUTE_ALL',
  PARTICIPANTS_SELF_BUTTONS: 'PARTICIPANTS_SELF_BUTTONS',
  PROFILE_SETTINGS: 'PROFILE_SETTINGS',
  RECENT_CONTACTS_MESSAGE_OPTIONS: 'RECENT_CONTACTS_MESSAGE_OPTIONS',
  RECENT_MORE_OPTIONS: 'RECENT_MORE_OPTIONS',
  ROOMS_MORE_OPTIONS: 'ROOMS_MORE_OPTIONS',
  ROOMS_SORT: 'ROOMS_SORT',
  ROOM_ADVANCED: 'ROOM_ADVANCED',
  ROOM_CHAT: 'ROOM_CHAT',
  ROOM_DO_NOT_DISTURB: 'ROOM_DO_NOT_DISTURB',
  ROOM_LAYOUT: 'ROOM_LAYOUT',
  ROOM_LOADING_UI: 'ROOM_LOADING_UI',
  ROOM_LOCK: 'ROOM_LOCK',
  ROOM_PARTICIPANTS: 'ROOM_PARTICIPANTS',
  ROOM_RAISE_HAND: 'ROOM_RAISE_HAND',
  ROOM_SHARE_SCREEN: 'ROOM_SHARE_SCREEN',
  SELF_AUTO_GAIN_CONTROL: 'SELF_AUTO_GAIN_CONTROL',
  SELF_BACKGROUND_NOISE: 'SELF_BACKGROUND_NOISE',
  SELF_ECHO_CANCELLATION: 'SELF_ECHO_CANCELLATION',
  SELF_LOW_BITRATE_MODE: 'SELF_LOW_BITRATE_MODE',
  SELF_MICROPHONE_SENSITIVITY: 'SELF_MICROPHONE_SENSITIVITY',
  SELF_MICROPHONE_VOLUME: 'SELF_MICROPHONE_VOLUME',
  SELF_NOISE_SUPPRESSION: 'SELF_NOISE_SUPPRESSION',
  SELF_SET_AUDIO_FLAGS: 'SELF_SET_AUDIO_FLAGS',
  SELF_SPEAKER_VOLUME: 'SELF_SPEAKER_VOLUME',
  SELF_VIDEO_QUALITY: 'SELF_VIDEO_QUALITY',
  SENTRY: 'SENTRY',
  USER_DISPLAY_BANNER: 'USER_DISPLAY_BANNER',
  USER_VARIABLES: 'USER_VARIABLES',
  WINDOW_EXPOSE_ROOM_SESSION: 'WINDOW_EXPOSE_ROOM_SESSION',
  WINDOW_EXPOSE_STORES: 'WINDOW_EXPOSE_STORES',
} as const satisfies Record<Uppercase<string>, Uppercase<string>>

export type FeatureName = keyof typeof FEATURES

type FeatureMap = Partial<CreateMutable<typeof FEATURES>>

const isFeatureName = (feature: string): feature is FeatureName => {
  return feature in FEATURES
}

const addFeatureToMap = (
  feature: FeatureName,
  disabledFeatures: FeatureMap,
) => {
  // @ts-expect-error - we know that the feature is a valid feature name
  disabledFeatures[feature] = feature
}

export const initialDisabledEnvFeatures = () => {
  const disabledEnvFeatures = VITE_DISABLED_FEATURES?.split(',')
  const disabledFeatures: FeatureMap = {}
  disabledEnvFeatures.forEach(feat => {
    const feature = feat.trim()
    if (isFeatureName(feature)) {
      addFeatureToMap(feature, disabledFeatures)
    }
  })
  return disabledFeatures
}

interface Actions {
  actions: {
    _enableAllFeatures: () => void
    _setFeatureDisabled: (name: FeatureName, disabled?: boolean) => void
  }
}

interface State {
  disabled: FeatureMap
}

const initialState: State = {
  disabled: initialDisabledEnvFeatures(),
}

type Store = Actions & State

const stateCreatorFn: StateCreator<Store> = set => ({
  ...initialState,
  actions: {
    _enableAllFeatures: () => {
      set({ disabled: {} })
    },
    _setFeatureDisabled: (name: FeatureName, disabled = true) => {
      set(state => {
        const disabledCopy = { ...state.disabled }
        if (disabled && isFeatureName(name)) {
          addFeatureToMap(name, disabledCopy)
        } else {
          delete disabledCopy[name]
        }
        return { disabled: disabledCopy }
      })
    },
  },
})

export const useFeaturesStore = create<Store>()(stateCreatorFn)
export const useFeaturesStoreActions = () => useFeaturesStore.getState().actions

// for convenience to use when Feature is not a component
export const isFeatureDisabled = (featureName: FeatureName) =>
  useFeaturesStore.getState().disabled[featureName] === FEATURES[featureName]

// for convenience to use when Feature is not a component and avoiding double negation
export const isFeatureEnabled = (featureName: FeatureName) =>
  useFeaturesStore.getState().disabled[featureName] !== FEATURES[featureName]
